import { AxDataTableDynamicRequestParams, AxDataTableRequestInput, AxDataTableResponse } from "@/lib/types";
import { AxiosResponse } from "axios";
import React, { useState } from "react";
import { AxDataTableHeader } from "../common/AxDataTableHeader";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Pagination } from "../common/Pagination";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { RowSelectionState } from "@tanstack/react-table";
import { AxDataTableFloatingDock } from "../dataTable/AxDataTableFloatingDock";
import { cn } from "@/lib/utils";

interface AxMediaViewerProps<TData> {
  uniqQueryKey: string;
  itemKey: keyof TData;
  fetcher: (
    input: AxDataTableRequestInput,
    dynamicRequestParams?: AxDataTableDynamicRequestParams
  ) => Promise<AxiosResponse<AxDataTableResponse<TData>>>;
  dynamicRequestParams?: AxDataTableDynamicRequestParams;
  extraHeader?: React.ReactNode;
  renderItem: (item: TData) => React.ReactNode;
  onDeleteItems?: (ids: any[]) => Promise<void>;
  deleting?: boolean;
  noResultsText?: string;
  className?: string;
  clientSideFilter?: (data: TData[]) => TData[];
}

const DEFAULT_PAGE_SIZE = 25;

export function AxMediaViewer<TData>({
  uniqQueryKey,
  fetcher,
  itemKey,
  dynamicRequestParams,
  extraHeader,
  renderItem,
  onDeleteItems,
  deleting,
  noResultsText,
  className,
  clientSideFilter,
}: AxMediaViewerProps<TData>) {
  const { t } = useTranslation();
  const [input, setInput] = useState<AxDataTableRequestInput>({
    numberOfObjectsPerPage: DEFAULT_PAGE_SIZE,
    pageNumber: 1,
    search: "",
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data, error, isLoading, isRefetching, refetch } = useQuery({
    queryKey: [uniqQueryKey, input, dynamicRequestParams],
    queryFn: () => fetcher(input, dynamicRequestParams),
    placeholderData: keepPreviousData,
    refetchOnMount: true,
  });

  const dataResult = clientSideFilter
    ? clientSideFilter(data?.data.result || [])
    : ((data?.data.result || []) as TData[]);

  const pageSizeOptions = data?.data?.dropdownList || [];
  const isUiLoading = isRefetching || isLoading;

  const onDeselectAll = () => setRowSelection({});

  const deleteAndRefresh = async (ids: string[]) => {
    if (onDeleteItems) {
      await onDeleteItems(ids);
      setRowSelection({});
      refetch();
    }
  };

  const toggleItemSelection = (id: string) => {
    setRowSelection((prev) => {
      if (prev[id]) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [id]: true,
      };
    });
  };

  const renderItemWithSelection = (item: TData) => {
    const id = String(item[itemKey]);
    return (
      <div className="relative">
        {onDeleteItems && (
          <div
            className="absolute top-2 left-2 z-10 checkbox-container"
            onClick={(_) => {
              toggleItemSelection(id);
            }}
          >
            <Checkbox
              checked={!!rowSelection[id]}
              onCheckedChange={(value) => {
                setRowSelection((prev) => ({
                  ...prev,
                  [id]: !!value,
                }));
              }}
              aria-label="Select item"
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </div>
        )}
        {renderItem(item)}
      </div>
    );
  };

  return (
    <div>
      <AxDataTableHeader
        totalRecord={data?.data.totalRecord || 0}
        pageSizeOptions={pageSizeOptions}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        filterValue={input.search || ""}
        onFilterChange={(value) => {
          setInput((prevInput) => ({
            ...prevInput,
            search: value,
          }));
        }}
        onPageSizeChange={(value) => {
          setInput((prevInput) => ({
            ...prevInput,
            numberOfObjectsPerPage: value,
          }));
        }}
        onRefreshClick={refetch}
        isLoading={isUiLoading}
        extraHeader={extraHeader}
      />
      {error ? (
        <Alert className="mt-2" variant="destructive">
          <AlertTitle>{t("common.error")}</AlertTitle>
          <AlertDescription>{error?.message || t("dataTable.error")}</AlertDescription>
        </Alert>
      ) : null}
      <div className={cn("axMediaViewerGrid mt-2", className)}>
        {dataResult.map((item) => (
          <div key={String(item[itemKey])}>{renderItemWithSelection(item)}</div>
        ))}
      </div>
      {!isLoading && dataResult.length === 0 && (
        <div className="flex justify-center items-center h-24 text-sm">{noResultsText || t("dataTable.noResults")}</div>
      )}
      <Pagination
        currentPageNumber={input.pageNumber}
        numberOfObjectsPerPage={input.numberOfObjectsPerPage}
        totalRecord={data?.data.totalRecord || 0}
        onPageChange={(newPageNumber) => {
          setInput((prevInput) => ({
            ...prevInput,
            pageNumber: newPageNumber,
          }));
        }}
        isLoading={isUiLoading}
      />
      {onDeleteItems && (
        <AxDataTableFloatingDock
          onDeleteRows={deleteAndRefresh}
          rowSelection={rowSelection}
          onDeselectAll={onDeselectAll}
          deleting={deleting}
        />
      )}
    </div>
  );
}
